import { jwtDecode } from 'jwt-decode';

import { ACCESS_TOKEN_KEY, ACCESS_TOKEN_EXPIRE_AT_KEY } from '@/constants';
import {
	AUTH_KEY,
	AUTH_SUBJECT_KEY,
	ACCESS_TOKEN_EXPIRATION_KEY,
	FEATURES_KEY,
	IS_SUBSCRIBED_KEY,
	REFRESH_TOKEN_KEY,
	REFRESH_TOKEN_EXPIRATION_KEY,
} from '@shared/auth/direct-auth';

/**
 * Claims worth carrying off the device, and why each one earns its place:
 *
 * - `aud`     -- separates a Canvasflow-hosted audience from one hosted by a
 *                tenant's own provider via third-party SSO. The single most
 *                useful field when debugging a PWA that isn't ours.
 * - `iss`     -- which IdP actually minted it; disambiguates `aud`.
 * - `iat`     -- when it was minted, which dates the session against whatever
 *                backend version was live at that moment.
 * - `exp`     -- was the session simply expired. Usually the first question.
 * - `nbf`/`jti` -- free to carry, occasionally decisive.
 * - `cf:entitlements` / `entitlements` -- content access, in both issuer
 *   conventions (see `observability/token-claims.ts` for why there are two).
 *
 * A whitelist, not a blocklist, for the same reason `token-claims.ts` is one:
 * these tokens routinely carry `email`, `name` and other identifying claims,
 * and a blocklist is only as good as your imagination about what an IdP might
 * add next.
 */
export const AUTH_SNAPSHOT_CLAIMS = [
	'aud',
	'iss',
	'iat',
	'exp',
	'nbf',
	'jti',
	'cf:entitlements',
	'entitlements',
] as const;

/**
 * Claims deliberately kept OUT of the redacted (exported) snapshot, and out
 * of `unlistedClaims` as well -- listing a name there reads as "consider
 * whitelisting this", which is the opposite of the intent.
 *
 * `sub` identifies the end user. The events pipeline's whole justification
 * for skipping consent gating is that it is non-identifying by construction
 * (see `observability.ts`), and while the export is justified differently --
 * a person deliberately triggers it -- keeping the two consistent means a
 * debug zip never carries a user identifier. It stays visible in the panel,
 * which is a local view of the viewer's own session.
 *
 * Trade-off accepted knowingly: you cannot tell *which* user a zip came from.
 * If that matters for a given investigation, read it off the panel on a call.
 */
const EXPORT_EXCLUDED_CLAIMS = new Set<string>(['sub']);

/**
 * Every localStorage key `buildAuthSnapshot` reads. Exported so a consumer
 * caching the snapshot (the panel's `useAuthSnapshot`) can build its cache
 * key from the same list rather than a hand-picked subset -- the two drifting
 * apart is exactly how the panel ended up showing stale `features` after a
 * subscription purchase, since only the token keys were being watched.
 */
export const AUTH_SNAPSHOT_STORAGE_KEYS = [
	AUTH_KEY,
	ACCESS_TOKEN_KEY,
	ACCESS_TOKEN_EXPIRATION_KEY,
	ACCESS_TOKEN_EXPIRE_AT_KEY,
	REFRESH_TOKEN_KEY,
	REFRESH_TOKEN_EXPIRATION_KEY,
	AUTH_SUBJECT_KEY,
	FEATURES_KEY,
	IS_SUBSCRIBED_KEY,
] as const;

export interface AuthSnapshot {
	/** `AUTH_KEY`, not "an access token exists" -- date-token (padi) and the
	 * password gate authenticate without ever minting one. */
	isAuthenticated: boolean;
	accessToken?: AccessTokenSnapshot;
	refreshToken?: {
		present: boolean;
		expiresAt: string | null;
		/** Unredacted (panel) only, same rule as the access token's `raw`. */
		raw?: string;
	};
	/** What the *client* believes about expiry, which is stored separately
	 * from the token's own `exp`. The two disagreeing is a real bug class and
	 * invisible unless they sit side by side. */
	clientExpiry: {
		accessTokenExpiresAt: string | null;
		accessTokenExpiredAt: string | null;
		refreshTokenExpiresAt: string | null;
	};
	/** `AUTH_SUBJECT_KEY` -- the only identity a date-token session has, and
	 * therefore a user identifier: unredacted (panel) only, same rule as
	 * `sub`. Absent from an exported snapshot entirely. */
	subject?: string | null;
	features: unknown;
	isSubscribed: boolean;
}

export interface AccessTokenSnapshot {
	present: boolean;
	/** False for an opaque or malformed token -- a non-JWT access token is a
	 * legitimate tenant configuration, not necessarily a fault. */
	decodable: boolean;
	/** Whitelisted claims only when `redacted`; the complete payload when not. */
	claims?: Record<string, unknown>;
	/** Names (never values) of claims present but outside the whitelist, so a
	 * claim worth capturing can be spotted and added without the export having
	 * leaked it in the meantime. Redacted mode only -- unredacted already
	 * shows everything. */
	unlistedClaims?: Array<string>;
	header?: Record<string, unknown>;
	/** Only ever populated when `redacted` is false. The panel is a local view
	 * of the viewer's own session; the zip is an artifact that travels. */
	raw?: string;
}

/**
 * One builder, two audiences -- the same single-source-of-truth arrangement
 * `debug-snapshot.ts` gives Events and Meta, so the panel and the zip cannot
 * quietly disagree about a session.
 *
 * `redacted: true`  -> the zip. Whitelisted claims, unlisted claim *names*,
 *                      never a token string.
 * `redacted: false` -> the devtools panel. Raw tokens (behind Copy buttons)
 *                      and the full decoded header/payload, because nothing
 *                      here leaves the machine it renders on.
 */
export function buildAuthSnapshot({
	redacted,
}: {
	redacted: boolean;
}): AuthSnapshot {
	const rawAccess = localStorage.getItem(ACCESS_TOKEN_KEY);
	const rawRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);

	return {
		isAuthenticated: localStorage.getItem(AUTH_KEY) === 'true',
		accessToken: rawAccess
			? buildAccessTokenSnapshot(rawAccess, redacted)
			: undefined,
		// Never decoded, in either mode: Cognito refresh tokens are opaque
		// strings rather than JWTs, so there is nothing to decode.
		refreshToken: rawRefresh
			? {
					present: true,
					expiresAt: localStorage.getItem(REFRESH_TOKEN_EXPIRATION_KEY),
					...(redacted ? {} : { raw: rawRefresh }),
				}
			: undefined,
		clientExpiry: {
			accessTokenExpiresAt: localStorage.getItem(
				ACCESS_TOKEN_EXPIRATION_KEY,
			),
			accessTokenExpiredAt: localStorage.getItem(
				ACCESS_TOKEN_EXPIRE_AT_KEY,
			),
			refreshTokenExpiresAt: localStorage.getItem(
				REFRESH_TOKEN_EXPIRATION_KEY,
			),
		},
		...(redacted
			? {}
			: { subject: localStorage.getItem(AUTH_SUBJECT_KEY) }),
		features: readJson(localStorage.getItem(FEATURES_KEY)),
		isSubscribed: localStorage.getItem(IS_SUBSCRIBED_KEY) === 'true',
	};
}

function buildAccessTokenSnapshot(
	raw: string,
	redacted: boolean,
): AccessTokenSnapshot {
	const header = decodeJwt(raw, { header: true });
	const payload = decodeJwt(raw);

	const base: AccessTokenSnapshot = {
		present: true,
		decodable: payload !== null,
		...(redacted ? {} : { raw }),
	};

	if (!payload) {
		return base;
	}

	if (!redacted) {
		return {
			...base,
			claims: payload,
			...(header ? { header } : {}),
		};
	}

	const claims: Record<string, unknown> = {};
	for (const claim of AUTH_SNAPSHOT_CLAIMS) {
		if (claim in payload) {
			claims[claim] = payload[claim];
		}
	}
	const allowed = new Set<string>(AUTH_SNAPSHOT_CLAIMS);
	const unlistedClaims = Object.keys(payload).filter(
		(k) => !allowed.has(k) && !EXPORT_EXCLUDED_CLAIMS.has(k),
	);

	return {
		...base,
		claims,
		...(unlistedClaims.length ? { unlistedClaims } : {}),
	};
}

// Via `jwt-decode`, already a dependency and already used by
// `observability/token-claims.ts` -- rather than hand-rolling the base64url
// handling a JWT needs (`atob` alone chokes on `-`/`_`). It throws on a
// malformed or opaque token; swallowed to null here, because a token this
// module can't parse must not take down the panel or the export that is
// trying to describe it.
function decodeJwt(
	token: string,
	options?: { header: true },
): Record<string, unknown> | null {
	try {
		const decoded = options
			? jwtDecode<Record<string, unknown>>(token, options)
			: jwtDecode<Record<string, unknown>>(token);
		return typeof decoded === 'object' && decoded !== null ? decoded : null;
	} catch {
		return null;
	}
}

function readJson(value: string | null): unknown {
	if (value === null) {
		return null;
	}
	try {
		return JSON.parse(value);
	} catch {
		return value;
	}
}
